// Tema antes de montar la app y de inyectar los estilos (arquitectura 2.16).
import '@plataforma/ui/tema-inicial';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Outlet, RouterProvider, useRouteError } from 'react-router';
import { ContextoApi } from '@plataforma/web/api';
import { Marco } from './componentes/Marco';
import { cargarConfig } from '@plataforma/web/config';
import '@plataforma/ui/base.css';
import '@plataforma/ui/panel.css';
import { Callback } from '@plataforma/web/Callback';
import { Entrar } from './paginas/Entrar';
import { RegistroConCuenta } from './paginas/RegistroConCuenta';
import {
  Cobros,
  ConvocatoriaDetalle,
  Convocatorias,
  MiPerfil,
  MisTrabajos,
} from './paginas/Portal';
import { Directorio } from './paginas/Directorio';
import { Requiere } from './permisos';
import { Usuarios } from './paginas/Usuarios';
import { NuevaOrden, OrdenTaller, Taller } from './paginas/Taller';
import { MisTareas } from './paginas/MisTareas';
import { Mostrador } from './paginas/Mostrador';
import { Desarmador } from './paginas/Desarmador';
import { EquipoProveedor } from './paginas/portal/Equipo';
import { MisErrores } from '@plataforma/web/errores';
import { InicioPortal } from './paginas/portal/Inicio';
import { Piezas, Ubicaciones } from './paginas/Piezas';
import { NuevaPublicacion, Publicaciones } from './paginas/Publicaciones';
import { Leads } from './paginas/Leads';
import { Pedidos } from './paginas/Pedidos';
import { MiSitio } from './paginas/Sitio';
import { MiSuscripcion } from './paginas/Suscripcion';
import { MisDominios } from './paginas/Dominios';
import { MisTickets, NuevoTicket, TicketDetalle } from './paginas/Tickets';
import { MiPrivacidad } from './paginas/Privacidad';
import { MiCuenta } from './paginas/MiCuenta';
import { PaginaPreparacion } from '@plataforma/web/Preparacion';
import { Ayuda } from './paginas/Ayuda';
import { MiNegocio } from './paginas/MiNegocio';
import type { ClavePermiso } from '@plataforma/permisos';
import type { ReactNode } from 'react';
import { NuevaSolicitud, Proveedores, SolicitudDetalle } from './paginas/Proveedores';
import { Subastas } from './paginas/Subastas';
import { CotizacionesAduana } from './paginas/CotizacionesAduana';
import { LoVigente } from '@plataforma/web/LoVigente';
import { Rentabilidad } from './paginas/Rentabilidad';
import { Canales } from './paginas/Canales';
import { EncargoDetalle, Encargos, NuevoEncargo } from './paginas/Encargos';
import { Tablero } from './paginas/Tablero';
import { Vehiculo } from './paginas/Vehiculo';
import { NuevoVehiculo, Vehiculos } from './paginas/Vehiculos';
import { ConsentimientoPendiente } from '@plataforma/web/legal';
import { crearGestor, ProveedorSesion, useSesion } from '@plataforma/web/sesion';

/** Ruta que pide el mismo permiso que su API. */
const con = (path: string, permiso: ClavePermiso, pantalla: ReactNode) => ({
  path,
  element: <Requiere permiso={permiso}>{pantalla}</Requiere>,
});

/** Rutas con sesión: sin usuario se muestra la entrada; un Super Admin no tiene lotero aquí. */
function ConSesion() {
  const { usuario, cargando, salir } = useSesion();
  if (cargando) return <div className="centro subtitulo">Cargando…</div>;
  if (!usuario) return <Entrar />;
  if (!usuario.tenantId && usuario.superadmin)
    return (
      <div className="centro">
        <div className="entrada">
          <h1>Esta cuenta no tiene lotero</h1>
          <p className="subtitulo">
            Admin Lotero es para las cuentas de un lotero. Super Admin entra por su propio panel.
          </p>
          <button className="boton" onClick={() => void salir()}>
            Salir
          </button>
        </div>
      </div>
    );
  if (!usuario.tenantId) return <RegistroConCuenta />;
  return (
    <ConsentimientoPendiente>
      <Outlet />
    </ConsentimientoPendiente>
  );
}

/** Un error inesperado de una pantalla no deja la app en blanco: se avisa y se puede volver. */
function ErrorPantalla() {
  const error = useRouteError() as Error | undefined;
  return (
    <div className="centro">
      <div className="entrada">
        <h1>Algo falló en esta pantalla</h1>
        <p className="subtitulo">
          No se perdió nada de lo que guardaste. Vuelve a intentarlo; si se repite, avísanos.
        </p>
        <p className="error" style={{ textAlign: 'left', fontSize: 13 }}>
          {error?.message ?? 'Error desconocido'}
        </p>
        <a className="boton primario" href="/">
          Volver al tablero
        </a>
      </div>
    </div>
  );
}

async function arrancar() {
  const raiz = createRoot(document.getElementById('raiz') as HTMLElement);
  try {
    const config = await cargarConfig();
    const gestor = crearGestor(config);
    const rutas = createBrowserRouter([
      { path: '/callback', element: <Callback gestor={gestor} /> },
      {
        element: <ConSesion />,
        errorElement: <ErrorPantalla />,
        children: [
          {
            element: <Marco />,
            children: [
              { index: true, element: <Tablero /> },
              {
                path: 'subastas',
                element: (
                  <Requiere permiso="montos.ver">
                    <Subastas />
                  </Requiere>
                ),
              },
              con('encargos', 'encargos.ver', <Encargos />),
              con('encargos/nuevo', 'encargos.pedir', <NuevoEncargo />),
              con('encargos/:id', 'encargos.ver', <EncargoDetalle />),
              con('importacion/cotizaciones', 'importacion.ver', <CotizacionesAduana />),
              con(
                'importacion/vigente',
                'importacion.ver',
                <LoVigente base="/sourcing/importacion/vigente" />,
              ),
              con('rentabilidad', 'montos.ver', <Rentabilidad />),
              { path: 'vehiculos', element: <Vehiculos /> },
              {
                path: 'vehiculos/nuevo',
                element: (
                  <Requiere permiso="vehiculos.registrar">
                    <NuevoVehiculo />
                  </Requiere>
                ),
              },
              { path: 'vehiculos/:id', element: <Vehiculo /> },
              {
                path: 'proveedores',
                element: (
                  <Requiere permiso="proveedores.ver">
                    <Outlet />
                  </Requiere>
                ),
                children: [
                  { index: true, element: <Proveedores /> },
                  { path: 'nueva', element: <NuevaSolicitud /> },
                  { path: 'directorio', element: <Directorio /> },
                  { path: ':id', element: <SolicitudDetalle /> },
                ],
              },
              con('usuarios', 'usuarios.administrar', <Usuarios />),
              con('taller', 'ordenes.ver', <Taller />),
              con('taller/nueva', 'ordenes.trabajar', <NuevaOrden />),
              con('taller/mis-tareas', 'ordenes.trabajar', <MisTareas />),
              con('desarme', 'piezas.administrar', <Desarmador />),
              con('mostrador', 'mostrador.vender', <Mostrador />),
              con('taller/:id', 'ordenes.ver', <OrdenTaller />),
              con('piezas', 'piezas.estado', <Piezas />),
              con('piezas/ubicaciones', 'piezas.estado', <Ubicaciones />),
              con('publicaciones', 'publicaciones.administrar', <Publicaciones />),
              con('publicaciones/nueva', 'publicaciones.administrar', <NuevaPublicacion />),
              con('canales', 'publicaciones.administrar', <Canales />),
              con('leads', 'leads.atender', <Leads />),
              con('pedidos', 'pedidos.ver', <Pedidos />),
              con('sitio', 'lotero.administrar', <MiSitio />),
              con('dominios', 'lotero.administrar', <MisDominios />),
              con('suscripcion', 'lotero.administrar', <MiSuscripcion />),
              // Lo ven los roles que configuran: dueño y gerente (arquitectura 2.25).
              con('preparacion', 'usuarios.administrar', <PaginaPreparacion />),
              con('ayuda', 'soporte.usar', <Ayuda />),
              con('ayuda/tickets', 'soporte.usar', <MisTickets />),
              con('ayuda/tickets/nuevo', 'soporte.usar', <NuevoTicket />),
              con('ayuda/tickets/:id', 'soporte.usar', <TicketDetalle />),
              con('ayuda/privacidad', 'soporte.usar', <MiPrivacidad />),
              con('negocio', 'soporte.usar', <MiNegocio />),
              { path: 'mi-cuenta', element: <MiCuenta /> },
              { path: 'portal', element: <InicioPortal /> },
              { path: 'portal/convocatorias', element: <Convocatorias /> },
              { path: 'portal/convocatorias/:id', element: <ConvocatoriaDetalle /> },
              { path: 'portal/trabajos', element: <MisTrabajos /> },
              { path: 'portal/cobros', element: <Cobros /> },
              { path: 'portal/perfil', element: <MiPerfil /> },
              con('portal/equipo', 'portal.perfil', <EquipoProveedor />),
              con('portal/errores', 'portal.perfil', <MisErrores ticketNuevo={null} />),
              con(
                'errores',
                'usuarios.administrar',
                <MisErrores ticketNuevo="/ayuda/tickets/nuevo" />,
              ),
            ],
          },
        ],
      },
    ]);
    raiz.render(
      <StrictMode>
        <ContextoApi.Provider value={config.api}>
          <ProveedorSesion gestor={gestor}>
            <RouterProvider router={rutas} />
          </ProveedorSesion>
        </ContextoApi.Provider>
      </StrictMode>,
    );
  } catch (e) {
    raiz.render(<p className="error">No se pudo arrancar el Admin: {(e as Error).message}</p>);
  }
  if ('serviceWorker' in navigator && import.meta.env.PROD)
    void navigator.serviceWorker.register('/sw.js');
}

void arrancar();
